import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { registrationsApi, RegistrationPublicResponse } from '../api/client';
import { CalendarIcon, MapPinIcon } from '@heroicons/react/24/outline';
import Logo from '../components/Logo';
import TicketRequestForm, { TicketFormData } from '../components/TicketRequestForm';
import { CasualTicketRequestForm, CasualTicketFormData } from '../components/CasualTicketRequestForm';
import { getErrorMessage } from '../utils/errorHandler';

const InvitationPage: React.FC = () => {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [invitation, setInvitation] = useState<RegistrationPublicResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [accepting, setAccepting] = useState(false);

  // Form state for regular events
  const [formData, setFormData] = useState<TicketFormData>({
    given_name: '',
    family_name: '',
    pronouns: '',
    email: '',
    company: '',
    role: '',
    phone: '',
    linkedin_url: '',
    relationship_type: '',
    dietary_allergies: '',
    dietary_preferences: '',
    non_dietary_requirements: '',
    how_heard: 'Event Invitation',
    code_of_conduct_accepted: false,
    privacy_policy_accepted: false,
    entry_requirement_accepted: false,
    newsletter_opt_in: false
  });

  // Form state for casual events
  const [casualFormData, setCasualFormData] = useState<CasualTicketFormData>({
    name: '',
    email: '',
    company: '',
    code_of_conduct_accepted: false,
    privacy_policy_accepted: false,
    entry_requirement_accepted: false,
    newsletter_opt_in: false
  });

  const loadInvitation = useCallback(async () => {
    if (!code) {
      setError('Invalid invitation link');
      setLoading(false);
      return;
    }

    // Check for URL parameters
    const urlError = searchParams.get('error');
    if (urlError) {
      if (urlError === 'invalid') {
        setError('This invitation link is not valid.');
      } else if (urlError === 'registered') {
        setError('You have already registered for this event using this invitation.');
      } else if (urlError === 'failed') {
        setError('An error occurred processing your request. Please try again.');
      }
      setLoading(false);
      return;
    }

    try {
      const data = await registrationsApi.getInvitationDetails(code);
      setInvitation(data);

      // Pre-fill form with invitation data
      // Split the full name into given and family names
      const nameParts = data.invitee_name ? data.invitee_name.split(' ') : [];
      const givenName = nameParts[0] || '';
      const familyName = nameParts.slice(1).join(' ') || '';

      setFormData(prev => ({
        ...prev,
        given_name: givenName,
        family_name: familyName,
        email: data.invitee_email,
        company: data.invitee_company || '',
        relationship_type: data.invitee_relationship_type || ''
      }));

      // Also pre-fill casual form data
      setCasualFormData(prev => ({
        ...prev,
        name: data.invitee_name || '',
        email: data.invitee_email,
        company: data.invitee_company || ''
      }));
    } catch (error) {
      setError(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  }, [code, searchParams]);

  useEffect(() => {
    loadInvitation();
  }, [loadInvitation]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!code || !invitation) return;

    setAccepting(true);
    try {
      if (invitation.event_type === 'casual') {
        // For casual events, split the full name
        const nameParts = casualFormData.name.trim().split(' ');
        const givenName = nameParts[0] || '';
        const familyName = nameParts.slice(1).join(' ') || '';

        await registrationsApi.acceptInvitation(code, {
          given_name: givenName,
          family_name: familyName,
          pronouns: '',
          email: casualFormData.email,
          company: casualFormData.company || '',
          role: '',
          relationship_type: '',
          dietary_requirements: '',
          non_dietary_requirements: '',
          how_heard: 'Event Invitation',
          newsletter_opt_in: casualFormData.newsletter_opt_in,
          entry_requirement_accepted: !!casualFormData.entry_requirement_accepted
        });
      } else {
        // For regular events
        let dietaryRequirements = '';
        const parts = [];
        if (formData.dietary_allergies) {
          parts.push(`Allergies/Intolerances: ${formData.dietary_allergies}`);
        }
        if (formData.dietary_preferences) {
          parts.push(`Dietary preferences: ${formData.dietary_preferences}`);
        }
        dietaryRequirements = parts.join('; ');

        await registrationsApi.acceptInvitation(code, {
          given_name: formData.given_name,
          family_name: formData.family_name,
          pronouns: formData.pronouns,
          email: formData.email,
          company: formData.company,
          role: formData.role,
          relationship_type: formData.relationship_type,
          dietary_requirements: dietaryRequirements,
          non_dietary_requirements: formData.non_dietary_requirements,
          how_heard: formData.how_heard,
          newsletter_opt_in: formData.newsletter_opt_in,
          entry_requirement_accepted: !!formData.entry_requirement_accepted
        });
      }

      navigate('/invitation-success');
    } catch (error) {
      alert(getErrorMessage(error));
    } finally {
      setAccepting(false);
    }
  };

  const handleDecline = async () => {
    if (!code) return;
    
    if (!window.confirm('Are you sure you want to decline this invitation?')) {
      return;
    }

    try {
      await registrationsApi.declineInvitation(code);
      navigate('/');
    } catch (error) {
      alert(getErrorMessage(error));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !invitation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Invalid Invitation</h1>
          <p className="text-gray-600 mb-6">{error || 'This invitation link is not valid.'}</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }


  if (invitation.has_registered) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center">
          <h1 className="text-2xl font-bold text-green-600 mb-4">Already Registered</h1>
          <p className="text-gray-600 mb-6">
            You have already registered for this event using this invitation.
          </p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
          >
            Go to Homepage
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <Logo size="medium" showCompanyName={true} />
        </div>
        
        {/* Header */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
          <div className="text-center mb-6">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">You're Invited!</h1>
            <p className="text-lg text-gray-600">
              {invitation.invitee_name} from {invitation.invitee_company}
            </p>
          </div>

          <div className="border-t border-b py-6 my-6">
            <h2 className="text-2xl font-semibold mb-4">{invitation.event_title}</h2>
            {invitation.event_description && (
              <p className="text-gray-600 mb-4 whitespace-pre-wrap">{invitation.event_description}</p>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="flex items-center gap-3">
                <CalendarIcon className="h-5 w-5 text-gray-400" />
                <span className="text-gray-700">
                  {format(new Date(invitation.event_date_time), 'PPPp')}
                </span>
              </div>
              <a
                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(invitation.event_venue)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-gray-700 hover:text-blue-600 transition-colors"
              >
                <MapPinIcon className="h-5 w-5 text-gray-400" />
                <span>{invitation.event_venue}</span>
              </a>
            </div>
          </div>

          <div className="text-center">
            <p className="text-gray-600 mb-6">
              Please complete the registration form below to secure your spot.
            </p>
          </div>
        </div>

        {/* Registration Form */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h3 className="text-xl font-semibold mb-6">Registration Details</h3>
          
          {invitation.event_type === 'casual' ? (
            <CasualTicketRequestForm
              formData={casualFormData}
              onChange={setCasualFormData}
              onSubmit={handleSubmit}
              submitButtonText="Accept Invitation"
              isSubmitting={accepting}
              showPrivacyPolicy={true}
              autoFocus={false}
              entryRequirement={invitation.event_entry_requirement}
            />
          ) : (
            <TicketRequestForm
              formData={formData}
              onChange={setFormData}
              onSubmit={handleSubmit}
              submitButtonText="Accept Invitation"
              isSubmitting={accepting}
              showPrivacyPolicy={true}
              autoFocus={false}
              entryRequirement={invitation.event_entry_requirement}
            />
          )}
          
          <div className="flex justify-start mt-6">
            <button
              type="button"
              onClick={handleDecline}
              className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              Decline Invitation
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvitationPage;